import React, { ReactElement } from "react";
import { Button } from "web/react/emo/button";
import { CheckoutProductCard } from "web/react/emo/checkout-product-card/checkout-product-card";
import { CircleLogo } from "web/react/emo/circle-logo";
import { HStack } from "web/react/emo/h-stack";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { gettext } from "web/script/modules/django-i18n";
import { RetailerOrderHistoryDetailsSerializer } from "web/types/serializers";
import * as styles from "./retailer-order-details.css";

export function RetailerOrderDetails({
    order,
}: {
    order: RetailerOrderHistoryDetailsSerializer;
}): ReactElement {
    return (
        <VStack spacing="sm">
            <HStack spacing="sm" align="center">
                <CircleLogo
                    size="md"
                    name={order.retailer_name}
                    imageURL={order.retailer_image || undefined}
                />
                <div>
                    <Text as="p" textStyle="body">{`${gettext(
                        "order_history.order_details.shipped_by"
                    )} ${order.retailer_name}`}</Text>
                    <Text as="p" textStyle="body-xs" color="secondary">{`${gettext(
                        "order_history.orders.retailer_order_no"
                    )}: ${order.order_no}`}</Text>
                </div>
            </HStack>

            <VStack spacing="sm">
                {order.product_order_lines.map((product) => (
                    <CheckoutProductCard
                        key={product.cart_item.buy_option_id}
                        size={product.cart_item.size}
                        title={order.retailer_name}
                        imageAlt={order.retailer_name}
                        imageSrc={product.product_image_url || ""}
                        colour={product.cart_item.color || undefined}
                        price={{ inStock: true, fullPrice: product.amount }}
                    />
                ))}
            </VStack>

            <div className={styles.contentHolder}>
                <VStack spacing="xxs">
                    <HStack spacing="xs" justify="space-between">
                        <Text as="p" textStyle="body-s-bold">
                            {order.shipping_details.remote_title}
                        </Text>
                        <Text as="p" textStyle="body-s-bold">
                            {order.shipping_details.amount}
                        </Text>
                    </HStack>

                    {order.shipping_details.estimated_time_in_transit && (
                        <Text as="p" textStyle="body-s">
                            {order.shipping_details.estimated_time_in_transit}
                        </Text>
                    )}
                </VStack>
            </div>

            <Text as="p" textStyle="body-s" dataTestId="retailer-disclaimer">
                <span
                    dangerouslySetInnerHTML={{
                        __html: gettext("order_history.order_details.retailer_disclaimer", {
                            retailer_name: order.retailer_name,
                            tc_props: order.retailer_details.tc_url
                                ? `href="${order.retailer_details.tc_url}" class="${styles.inlineLink}"`
                                : "",
                            privacy_policy_props: order.retailer_details.privacy_policy_url
                                ? `href="${order.retailer_details.privacy_policy_url}" class="${styles.inlineLink}"`
                                : "",
                        }),
                    }}
                />
            </Text>

            <div>
                <Button
                    title={order.retailer_name}
                    width="full"
                    icon="external-link"
                    iconPosition="right"
                    href={order.retailer_website || undefined}
                />
            </div>
        </VStack>
    );
}
